/**
 * Sales Activity Tab
 * 영업 활동 히스토리 탭
 */

import React, { useState } from 'react';
import {
  SalesActivity,
  ActivityType,
  getActivityTypeText,
  getActivityTypeIcon,
  formatDateTime,
} from '../../models/SalesLead';

interface SalesActivityTabProps {
  leadId: string;
  activities: SalesActivity[];
  onAddActivity: (leadId: string, activityType: ActivityType, content: string) => Promise<boolean>;
  onRefresh: () => void;
}

const activityTypes: { value: ActivityType; label: string; icon: string }[] = [
  { value: 'call', label: '전화 상담', icon: '📞' },
  { value: 'email', label: '이메일', icon: '📧' },
  { value: 'visit', label: '방문 상담', icon: '📍' },
  { value: 'demo', label: '데모/시연', icon: '🖥️' },
  { value: 'quote', label: '견적서 발송', icon: '📄' },
  { value: 'note', label: '메모', icon: '📌' },
];

export const SalesActivityTab: React.FC<SalesActivityTabProps> = ({
  leadId,
  activities,
  onAddActivity,
  onRefresh,
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedType, setSelectedType] = useState<ActivityType>('call');
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!content.trim()) {
      alert('활동 내용을 입력해주세요.');
      return;
    }

    setLoading(true);
    const success = await onAddActivity(leadId, selectedType, content);
    setLoading(false);

    if (success) {
      setContent('');
      setShowAddForm(false);
      onRefresh();
    }
  };

  const getActivityColor = (type: ActivityType): string => {
    const colors: Record<ActivityType, string> = {
      call: 'bg-blue-100 border-blue-300',
      email: 'bg-purple-100 border-purple-300',
      visit: 'bg-green-100 border-green-300',
      demo: 'bg-orange-100 border-orange-300',
      quote: 'bg-yellow-100 border-yellow-300',
      contract: 'bg-emerald-100 border-emerald-300',
      note: 'bg-gray-100 border-gray-300',
      status_change: 'bg-indigo-100 border-indigo-300',
    };
    return colors[type] || 'bg-gray-100 border-gray-300';
  };

  return (
    <div className="space-y-6">
      {/* Add Activity Button */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">상담 히스토리</h3>
        {!showAddForm && (
          <button
            onClick={() => setShowAddForm(true)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm flex items-center space-x-2"
          >
            <span>➕</span>
            <span>활동 추가</span>
          </button>
        )}
      </div>

      {/* Add Activity Form */}
      {showAddForm && (
        <div className="bg-gray-50 rounded-lg p-4 space-y-4">
          <h4 className="font-medium">새 활동 기록</h4>

          {/* Activity Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">활동 유형</label>
            <div className="flex flex-wrap gap-2">
              {activityTypes.map((type) => (
                <button
                  key={type.value}
                  onClick={() => setSelectedType(type.value)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    selectedType === type.value
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-indigo-400'
                  }`}
                >
                  {type.icon} {type.label}
                </button>
              ))}
            </div>
          </div>

          {/* Content Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">활동 내용</label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={`${getActivityTypeText(selectedType)} 내용을 입력하세요...`}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-2">
            <button
              onClick={() => {
                setShowAddForm(false);
                setContent('');
              }}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
            >
              취소
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading || !content.trim()}
              className={`px-4 py-2 rounded-md text-white ${
                loading || !content.trim()
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-indigo-600 hover:bg-indigo-700'
              }`}
            >
              {loading ? '저장 중...' : '저장'}
            </button>
          </div>
        </div>
      )}

      {/* Activities List */}
      {activities.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <p className="text-4xl mb-4">📝</p>
          <p>아직 활동 기록이 없습니다.</p>
          <p className="text-sm mt-1">상담 내용이나 진행 상황을 기록해보세요.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {activities.map((activity, index) => (
            <div
              key={activity.id}
              className={`relative pl-8 pb-4 ${
                index !== activities.length - 1 ? 'border-l-2 border-gray-200' : ''
              }`}
            >
              {/* Timeline Dot */}
              <div
                className={`absolute left-0 top-0 w-4 h-4 rounded-full -translate-x-1/2 ${getActivityColor(
                  activity.activity_type
                )} border-2`}
              ></div>

              {/* Activity Card */}
              <div className={`ml-4 p-4 rounded-lg ${getActivityColor(activity.activity_type)}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">
                    {getActivityTypeIcon(activity.activity_type)} {getActivityTypeText(activity.activity_type)}
                  </span>
                  <span className="text-sm text-gray-500">
                    {formatDateTime(activity.created_at)}
                  </span>
                </div>
                <p className="text-gray-700 whitespace-pre-wrap">{activity.content}</p>
                <p className="text-sm text-gray-500 mt-2">👤 {activity.created_by_name}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
